import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Task, TaskType } from "../../redux/type";
import { Check, X, ExternalLink, Clock, Award, Timer } from "lucide-react";
import {
  DECODER_THREAD_ID,
  FEED_WATCHER_THREAD_ID,
  GROUP_CHAT_ID,
  INTERFACE_ARCHITECT_THREAD_ID,
  LOREKEEPER_THREAD_ID,
  NETWORKING_EVENT_THREAD_ID,
  SIGNAL_BOOSTER_THREAD_ID,
  SYSTEM_BUILDER_THREAD_ID,
  UI_UX_ARCHITECT_THREAD_ID,
} from "../../utils/constants";

const groupTypes = [TaskType.DECODER, TaskType.NETWORKING_EVENT];

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #1a1e2b, #232a3d);
  color: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding: 2rem 1.5rem;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 -12px 30px rgba(0, 0, 0, 0.4);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  padding: 0.5rem;
  cursor: pointer;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, #ffffff, #e0e0e0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled.p`
  font-size: 1.2rem;
  margin-top: 0.75rem;
  margin-bottom: 0rem;
  color: rgba(255, 255, 255, 0.75);
  white-space: pre-line; // ✅ respects \n\n from API
`;

const Countdown = styled.div<{ $late?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: ${({ $late }) => ($late ? "#FFA500" : "#ccc")}; // orange if late
`;

const RewardBadge = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: #ccff00;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 2rem;

  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 0.875rem 1.25rem;
  background: ${({ $primary }) =>
    $primary
      ? "linear-gradient(135deg, #ccff00, #3B82F6)"
      : "rgba(255, 255, 255, 0.08)"};
  color: ${({ $primary }) => ($primary ? "#111827" : "white")};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${({ $primary }) =>
      $primary
        ? "linear-gradient(135deg, #5AD6A5, #2563EB)"
        : "rgba(255, 255, 255, 0.15)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TaskTypeBadge = styled.div<{ type: TaskType }>`
  position: absolute;
  top: -2.1rem;
  left: 0;
  font-size: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;

  background: ${({ type }) =>
    [TaskType.LOREKEEPER].includes(type)
      ? "rgba(110, 231, 183, 0.1)"
      : [
          TaskType.FEED_WATCHER,
          TaskType.NETWORKING_EVENT,
          TaskType.SIGNAL_BOOSTER,
        ].includes(type)
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(168, 85, 247, 0.1)"};

  color: ${({ type }) =>
    [TaskType.LOREKEEPER].includes(type)
      ? "#ccff00"
      : [
          TaskType.FEED_WATCHER,
          TaskType.NETWORKING_EVENT,
          TaskType.SIGNAL_BOOSTER,
        ].includes(type)
      ? "#3b82f6"
      : "#a855f7"};
`;

export const formatCountdown = (end: Date) => {
  const now = new Date();
  const diff = new Date(end).getTime() - now.getTime();

  if (diff <= 0) return "Expired";

  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor((diff / 1000 / 60) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return `${hours}h ${minutes}m ${seconds}s`;
};

const GROUP_THREAD_IDS = {
  [TaskType.DECODER]: DECODER_THREAD_ID,
  [TaskType.LOREKEEPER]: LOREKEEPER_THREAD_ID,
  [TaskType.SIGNAL_BOOSTER]: SIGNAL_BOOSTER_THREAD_ID,
  [TaskType.FEED_WATCHER]: FEED_WATCHER_THREAD_ID,
  [TaskType.NETWORKING_EVENT]: NETWORKING_EVENT_THREAD_ID,
  [TaskType.UI_UX_ARCHITECT]: UI_UX_ARCHITECT_THREAD_ID,
  [TaskType.INTERFACE_ARCHITECT]: INTERFACE_ARCHITECT_THREAD_ID,
  [TaskType.SYSTEM_BUILDER]: SYSTEM_BUILDER_THREAD_ID,
};

const TaskDetailsModal = ({
  task,
  onClose,
  onValidate,
}: {
  task: Task;
  onClose: () => void;
  onValidate: (answer: string) => void;
}) => {
  const [userAnswer, setUserAnswer] = useState("");
  const [countdown, setCountdown] = useState("");
  const [isLate, setIsLate] = useState(false);

  const isGroupTask = groupTypes.includes(task.type);
  const hasAnswer = task.answer && !isGroupTask;

  useEffect(() => {
    if (!task.endDate) return;

    const updateCountdown = () => {
      const diff = new Date(task.endDate!).getTime() - Date.now();

      if (diff <= 0) {
        setCountdown("Late");
        setIsLate(true);
      } else {
        setCountdown(formatCountdown(task.endDate!));
        setIsLate(false);
      }
    };

    updateCountdown(); // initial
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [task.endDate]);

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 400, damping: 32 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>

          <Header>
            <TaskTypeBadge type={task.type}>{task.type}</TaskTypeBadge>
            <Title>{task.title}</Title>
            {task.endDate && (
              <Countdown $late={isLate}>
                <Timer size={16} color={isLate ? "#FFA500" : "#ccc"} />
                {isLate ? "Late" : `Ends in: ${countdown}`}
              </Countdown>
            )}
            {/* <RewardBadge>
              <RewardItem>
                <Award />
                {task.xpReward} XP
              </RewardItem>
              <RewardItem>
                <Clock />
                {task.timeReward} mins
              </RewardItem>
            </RewardBadge> */}
          </Header>
          <Description>{task.description}</Description>

          {hasAnswer && (
            <Input
              placeholder="Enter your answer here..."
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
            />
          )}

          <ButtonRow>
            {
              <Button
                onClick={() => {
                  const link = `https://t.me/c/${GROUP_CHAT_ID.toString().replace(
                    "-100",
                    ""
                  )}/${GROUP_THREAD_IDS[task.type]}/${task.messageId}`;

                  (window as any).Telegram.WebApp.openTelegramLink(link);
                }}
              >
                <ExternalLink />
                Go to Task
              </Button>
            }
          </ButtonRow>
        </Modal>
      </Backdrop>
    </AnimatePresence>
  );
};

export default TaskDetailsModal;
